import { Mark, mergeAttributes } from '@tiptap/core'
import { TextSelection } from '@tiptap/pm/state'
import type { EditorState, Transaction } from '@tiptap/pm/state'
import { DELETION, INSERTION } from './transcriptDoc'

/**
 * Tracked changes as editor marks.
 *
 * Deletion is a MARK, not a removal: the characters stay in the document at
 * their original position, carrying a mark that renders them struck through.
 * There is no diffing and no reconstruction — what is on screen is what the
 * document holds, and what is stored is the same document.
 */

export const Deletion = Mark.create({
  name: DELETION,
  /* Typing beside struck wording must not join it. */
  inclusive: false,
  parseHTML() {
    return [{ tag: 'span[data-deletion]' }]
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        'data-deletion': 'true',
        title: 'Deleted — click to restore',
        class:
          'cursor-pointer text-mute line-through decoration-mute/70 transition-colors duration-150 ease-out hover:text-charcoal hover:decoration-charcoal',
      }),
      0,
    ]
  },
})

export const Insertion = Mark.create({
  name: INSERTION,
  inclusive: true,
  parseHTML() {
    return [{ tag: 'span[data-insertion]' }]
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        'data-insertion': 'true',
        class: 'bg-ink/[0.06] text-ink underline underline-offset-4',
      }),
      0,
    ]
  },
})

/**
 * Marks a range as deleted rather than deleting it.
 *
 * Wording the reader added themselves was never part of Portal's transcript,
 * so it is removed outright. Everything else is struck in place.
 *
 * Returns false when the range holds nothing that can be tracked, so the
 * caller can leave the document alone.
 */
export function trackDelete(
  state: EditorState,
  tr: Transaction,
  from: number,
  to: number,
): boolean {
  const deletionType = state.schema.marks[DELETION]
  const insertionType = state.schema.marks[INSERTION]
  if (!deletionType || from >= to) return false

  const strike: { from: number; to: number }[] = []
  const remove: { from: number; to: number }[] = []

  state.doc.nodesBetween(from, to, (node, pos) => {
    if (!node.isText) return
    const start = Math.max(from, pos)
    const end = Math.min(to, pos + node.nodeSize)
    if (end <= start) return

    const marks = node.marks.map((mark) => mark.type.name)
    if (insertionType && marks.includes(INSERTION)) remove.push({ from: start, to: end })
    else if (!marks.includes(DELETION)) strike.push({ from: start, to: end })
  })

  if (!strike.length && !remove.length) return false

  strike.forEach((range) => tr.addMark(range.from, range.to, deletionType.create()))

  /* Removals last and in reverse, so earlier positions stay valid. */
  remove
    .slice()
    .reverse()
    .forEach((range) => tr.delete(tr.mapping.map(range.from), tr.mapping.map(range.to)))

  return true
}

/** Puts the caret at a position, keeping it out of struck wording. */
export function placeCaret(tr: Transaction, pos: number): void {
  const target = Math.max(0, Math.min(pos, tr.doc.content.size))
  tr.setSelection(TextSelection.create(tr.doc, target))
}
